// Upload a supplier schedule, look at what the reader made of it, and confirm.
//
// THE ORDER OF THE SCREEN IS THE CONTROL. Nothing is written behind a charge
// until somebody has seen every line the reader found and pressed Confirm. That
// is not caution for its own sake: a mis-read schedule does not produce an ugly
// screen, it produces a wrong premium on an owner's statement that the owner has
// no way to check.
//
// So this dialog is deliberately slow in one place. It shows the reader's
// confidence, whether the amounts tied to a total printed on the document, every
// warning it raised, and every line including the ones it could not match to a
// car. An unmatched line is not hidden, because hiding it would make the totals
// look like they tie when they do not.
import { useCallback, useRef, useState } from 'react'
import { Upload, CircleAlert, CircleCheck, X } from 'lucide-react'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Spinner } from '../../lib/shadcn/spinner'
import { cn } from '../../lib/shadcn/utils'
import {
  useUploadScheduleDocument,
  useGetScheduleUpload,
  useConfirmScheduleUpload,
} from '../../hooks/backend/statements'
import { formatZar, formatInt } from '../../utils/statements'
import { lineLabel, monthLabel } from '../../utils/evidenceCoverage'

// Shown when the amounts the reader found do not add up to any total printed on
// the document. Confirm is switched off in that state (and the database refuses
// it as well), so the old wording, "check every amount before confirming", told
// the operator to do something the screen would not let them do. Reported as
// platform request #6 on 10 September 2026 against a Cartrack invoice. The steps
// say what to look for on the paper and which of the two ways out applies.
const UNRECONCILED_SUMMARY =
  'The amounts below do not add up to any total printed on the document, so nothing proves which figure is the charge for each car. It cannot be confirmed as read, and nothing has been recorded against any owner.'

const UNRECONCILED_STEPS = [
  'Open the PDF and find the total printed at the end of it: on a Cartrack invoice, the total including VAT; on an insurance schedule, the total monthly premium. Compare it with the amounts listed below.',
  'If the document has no total on it, or pages are missing, or it is a scan or a premium confirmation letter rather than the schedule or invoice itself, ask the supplier for the complete emailed PDF and upload that instead.',
  'If the document is complete and the amounts below are not the ones on it, the reader has misread it. Press Discard this document, then send the PDF through Report a problem so the reader can be corrected and the document read again.',
]

// Matches the reader's stored sentence for this state, old wording and new.
const UNRECONCILED_WARNING = /do not add up to any total printed/i

type Review = {
  upload_id: string
  line_code: string
  state: string
  filename: string
  supplier: string | null
  document_number: string | null
  schedule_date: string | null
  covers_from: string | null
  covers_to: string | null
  stated_total_zar: number | null
  reader_method: string | null
  reader_confidence: string | null
  reader_warnings: string[]
  note: string | null
  lines_found: number
  lines_matched: number
  lines_total_zar: number | null
  lines: {
    line_id: string
    plate: string | null
    vehicle_id: string | null
    matched_plate: string | null
    description: string | null
    amount_zar: number
    match_note: string | null
  }[]
}

// The file never leaves the browser as anything but base64 on the same request
// that stores it. readAsDataURL gives "data:application/pdf;base64,AAAA"; only
// the part after the comma is the file.
function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('That file could not be read from this device.'))
    reader.onload = () => {
      const raw = String(reader.result ?? '')
      const comma = raw.indexOf(',')
      resolve(comma >= 0 ? raw.slice(comma + 1) : raw)
    }
    reader.readAsDataURL(file)
  })
}

export function ScheduleUploadDialog({
  open,
  onOpenChange,
  lineCode,
  month,
  onConfirmed,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  lineCode: string
  month: string | null
  onConfirmed: () => void
}) {
  const upload = useUploadScheduleDocument()
  const review = useGetScheduleUpload<Review | null>()
  const confirm = useConfirmScheduleUpload()

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [read, setRead] = useState<Review | null>(null)
  const [supplier, setSupplier] = useState('')
  const [documentNumber, setDocumentNumber] = useState('')
  const [covers, setCovers] = useState('')
  const [done, setDone] = useState<{ lines: number; extracts: number; months: number } | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const reset = useCallback(() => {
    setBusy(false); setError(null); setRead(null); setDone(null)
    setSupplier(''); setDocumentNumber(''); setCovers('')
    if (fileRef.current) fileRef.current.value = ''
  }, [])

  const close = useCallback((v: boolean) => {
    if (!v) reset()
    onOpenChange(v)
  }, [onOpenChange, reset])

  const onFile = useCallback(async (file: File) => {
    setBusy(true); setError(null); setDone(null)
    try {
      const b64 = await toBase64(file)
      const res = await upload.trigger(
        {
          line_code: lineCode,
          filename: file.name,
          content_type: file.type || 'application/pdf',
          content_base64: b64,
        },
        { skipCache: true },
      ) as { upload_id?: string | null; state?: string; note?: string | null }

      if (!res?.upload_id) {
        setError(res?.note ?? 'The document was not stored. Nothing has changed.')
        return
      }
      const full = await review.trigger({ upload_id: res.upload_id }, { skipCache: true }) as Review | null
      if (full) {
        setRead(full)
        setSupplier(full.supplier ?? '')
        setDocumentNumber(full.document_number ?? '')
        setCovers(full.covers_from ?? (month ? `${month}-01` : ''))
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }, [lineCode, month, review, upload])

  const doConfirm = useCallback(async () => {
    if (!read) return
    setBusy(true); setError(null)
    try {
      const res = await confirm.trigger(
        {
          upload_id: read.upload_id,
          supplier: supplier.trim() || null,
          document_number: documentNumber.trim() || null,
          covers_from: covers || null,
          covers_to: covers || null,
        },
        { skipCache: true },
      ) as { lines_written?: number; extracts_written?: number; months_closed?: number }
      setDone({
        lines: Number(res?.lines_written ?? 0),
        extracts: Number(res?.extracts_written ?? 0),
        months: Number(res?.months_closed ?? 0),
      })
      onConfirmed()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }, [confirm, covers, documentNumber, onConfirmed, read, supplier])

  const doReject = useCallback(async () => {
    if (!read) return
    setBusy(true); setError(null)
    try {
      await confirm.trigger({ upload_id: read.upload_id, reject: true }, { skipCache: true })
      close(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }, [close, confirm, read])

  const unmatched = (read?.lines ?? []).filter((l) => !l.vehicle_id)
  // The reader stores its own sentence about an unreconciled reading, and
  // uploads read before 3 October 2026 carry the old one, which ended "check
  // every amount before confirming" above a Confirm button that is switched
  // off. The box above now says the same thing with the steps, so the stored
  // sentence is not repeated beneath it.
  const shownWarnings = (read?.reader_warnings ?? []).filter(
    (w) => !(read?.stated_total_zar === null && UNRECONCILED_WARNING.test(w)),
  )

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {lineLabel(lineCode)} schedule{month ? `, ${monthLabel(month)}` : ''}
          </DialogTitle>
          <DialogDescription>
            The reader proposes what the document says. Nothing is recorded against
            an owner until you confirm it.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
            <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {done ? (
          <div className="flex flex-col gap-2 rounded-md border border-emerald-600/30 bg-emerald-50 p-4 text-sm text-emerald-900">
            <div className="flex items-center gap-2 font-medium">
              <CircleCheck className="h-4 w-4" /> Confirmed.
            </div>
            <p className="leading-relaxed">
              {formatInt(done.lines)} vehicle line{done.lines === 1 ? '' : 's'} recorded from
              this document, and {formatInt(done.extracts)} charge
              {done.extracts === 1 ? '' : 's'} across {formatInt(done.months)} month
              {done.months === 1 ? '' : 's'} now have it behind them.
              {done.extracts === 0 && ' No charge was open for these cars in the month this document covers, so the schedule is on file and nothing changed on a statement.'}
            </p>
          </div>
        ) : !read ? (
          <div className="flex flex-col gap-3">
            <label className="text-xs font-medium text-muted-foreground" htmlFor="schedule-file">
              The supplier's own PDF, not a scan or a photograph. The reader needs a
              text layer, and a printed-then-scanned copy has none.
            </label>
            <Input
              id="schedule-file"
              ref={fileRef}
              type="file"
              accept="application/pdf"
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0]
                if (f) void onFile(f)
              }}
            />
            {busy && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Spinner className="h-4 w-4" /> Storing and reading the document…
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="flex flex-col gap-1">
                <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Supplier</span>
                <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="MiWay" />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Policy or account</span>
                <Input value={documentNumber} onChange={(e) => setDocumentNumber(e.target.value)} placeholder="112629424" />
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Month it covers</span>
                <Input type="date" value={covers} onChange={(e) => setCovers(e.target.value)} />
              </div>
            </div>

            <div className="rounded-md border border-border bg-muted/40 p-3 text-xs leading-relaxed">
              <div className="font-medium text-foreground">
                {read.filename} · {formatInt(read.lines_found)} vehicle line
                {read.lines_found === 1 ? '' : 's'}, {formatInt(read.lines_matched)} matched to a car
                {read.lines_total_zar !== null && ` · ${formatZar(read.lines_total_zar)}`}
              </div>
              <div className="mt-1 text-muted-foreground">
                {read.stated_total_zar !== null
                  ? `The amounts add up to ${formatZar(read.stated_total_zar)}, which is a total printed on the document itself. That is what makes this reading provable rather than plausible.`
                  : UNRECONCILED_SUMMARY}
              </div>
              {read.stated_total_zar === null && (
                <ol className="mt-2 list-decimal space-y-1 pl-5 text-foreground">
                  {UNRECONCILED_STEPS.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              )}
              {read.reader_method && (
                <div className="mt-1 text-muted-foreground">Read by {read.reader_method}.</div>
              )}
            </div>

            {shownWarnings.length > 0 && (
              <ul className="flex flex-col gap-1.5 rounded-md border border-amber-500/30 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
                {shownWarnings.map((w, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CircleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full min-w-[520px] text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 font-medium">Registration</th>
                    <th className="px-3 py-2 font-medium">On the document</th>
                    <th className="px-3 py-2 text-right font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {read.lines.map((l) => (
                    <tr
                      key={l.line_id}
                      className={cn(
                        'border-b border-border/60 align-top last:border-0',
                        !l.vehicle_id && 'bg-amber-50/60',
                      )}
                    >
                      <td className="whitespace-nowrap px-3 py-2 font-medium">
                        {l.plate ?? '—'}
                        {!l.vehicle_id && (
                          <div className="mt-0.5 max-w-[220px] text-[11px] font-normal leading-snug text-amber-800">
                            {l.match_note ?? 'Not in the fleet register. Nothing will be recorded against it.'}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{l.description ?? '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{formatZar(l.amount_zar)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {unmatched.length > 0 && (
              <p className="text-xs leading-relaxed text-muted-foreground">
                {formatInt(unmatched.length)} line{unmatched.length === 1 ? '' : 's'} could not be
                tied to a vehicle and will be skipped. They are shown rather than
                dropped, because a document whose totals do not tie is worth knowing about.
              </p>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          {done ? (
            <Button onClick={() => close(false)}>Close</Button>
          ) : read ? (
            <>
              <Button variant="ghost" onClick={() => void doReject()} disabled={busy}>
                <X className="mr-1.5 h-4 w-4" /> Discard this document
              </Button>
              <Button
                onClick={() => void doConfirm()}
                disabled={busy || read.lines_matched === 0 || read.stated_total_zar === null}
                title={
                  read.stated_total_zar === null
                    ? 'Confirm is off because the amounts do not add up to any total printed on the document. The steps above say what to check and what to do next.'
                    : undefined
                }
              >
                {busy ? <Spinner className="mr-1.5 h-4 w-4" /> : <Upload className="mr-1.5 h-4 w-4" />}
                Confirm {formatInt(read.lines_matched)} vehicle
                {read.lines_matched === 1 ? '' : 's'}
              </Button>
            </>
          ) : (
            <Button variant="ghost" onClick={() => close(false)} disabled={busy}>Cancel</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
